import { useState, useEffect } from "react";
import { API, authFetch, useAuth } from "@/App";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Clock, AlertTriangle, Search,
  MessageSquare, ShoppingCart, Store, Receipt, Eye,
  StickyNote, User, CalendarDays, Flag, ArrowUp, ArrowDown,
  Minus, ChevronDown,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";

const PRIORITY_CONFIG = {
  CRITICAL: { color: "bg-red-600 text-white", icon: ArrowUp, label: "Critical" },
  HIGH: { color: "bg-orange-500 text-white", icon: ArrowUp, label: "High" },
  MEDIUM: { color: "bg-amber-400 text-amber-900", icon: Minus, label: "Medium" },
  LOW: { color: "bg-slate-300 text-slate-700", icon: ArrowDown, label: "Low" },
};

const PriorityBadge = ({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.MEDIUM;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.color} text-[10px] gap-0.5`}>
      <Icon className="w-2.5 h-2.5" /> {cfg.label}
    </Badge>
  );
};

const ApprovalsPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();
  const [data, setData] = useState({ dealers: [], orders: [], expenses: [], leaves: [], grievances: [] });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("orders");
  const [search, setSearch] = useState("");
  const [actionDialog, setActionDialog] = useState(null);
  const [notes, setNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [grievanceDialog, setGrievanceDialog] = useState(null);
  const [grievanceUpdate, setGrievanceUpdate] = useState({ status: "", resolution: "", assigned_to: "" });

  const fetchApprovals = async () => {
    try {
      const res = await authFetch(`${API}/approvals?include_held=true`);
      const json = await res.json();
      if (json.success) { setData(json.data); setCounts(json.data.counts || {}); }
    } catch { toast.error("Failed to load approvals"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchApprovals(); }, []);

  const handleAction = async () => {
    if (!actionDialog) return;
    setProcessing(true);
    const { type, id, action } = actionDialog;
    try {
      let url = "";
      const body = { notes };
      if (type === "order") url = `${API}/orders/${id}/${action === "approve" ? "approve" : action === "reject" ? "reject" : "hold"}`;
      else if (type === "dealer") url = `${API}/parties/${id}/${action === "approve" ? "approve" : action === "reject" ? "reject" : "hold"}`;
      else if (type === "expense") url = `${API}/expenses/${id}/${action === "approve" ? "approve" : "reject"}`;
      else if (type === "leave") url = `${API}/leaves/${id}/${action === "approve" ? "approve" : action === "reject" ? "reject" : "hold"}`;

      const res = await authFetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (res.ok) {
        toast.success(`${action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Put on Hold"} successfully`);
        setActionDialog(null); setNotes(""); fetchApprovals();
      } else { const j = await res.json(); toast.error(j.detail || "Failed"); }
    } catch { toast.error("Action failed"); }
    finally { setProcessing(false); }
  };

  const handleGrievanceUpdate = async () => {
    if (!grievanceDialog) return;
    setProcessing(true);
    try {
      const res = await authFetch(`${API}/grievances/${grievanceDialog.grievance_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(grievanceUpdate),
      });
      if (res.ok) { toast.success("Grievance updated"); setGrievanceDialog(null); fetchApprovals(); }
    } catch { toast.error("Update failed"); }
    finally { setProcessing(false); }
  };

  const openAction = (type, id, action, label) => { setActionDialog({ type, id, action, itemLabel: label }); setNotes(""); };

  const viewHistory = async (type, id) => {
    setHistoryDialog({ type, id });
    try {
      if (type === "order") { const r = await authFetch(`${API}/orders/${id}/approval-history`); const j = await r.json(); setHistoryData(j.data || []); }
      else { const r = await authFetch(`${API}/parties/${id}`); const j = await r.json(); setHistoryData(j.data?.approval_history || []); }
    } catch { setHistoryData([]); }
  };

  const getStatusBadge = (s) => {
    const st = (s || "").toUpperCase();
    if (st === "PLACED" || st === "PENDING") return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]"><Clock className="w-2.5 h-2.5 mr-1" />Pending</Badge>;
    if (st === "APPROVED" || st === "ACTIVE") return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]"><CheckCircle className="w-2.5 h-2.5 mr-1" />Approved</Badge>;
    if (st === "CANCELLED" || st === "REJECTED") return <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]"><XCircle className="w-2.5 h-2.5 mr-1" />Rejected</Badge>;
    if (st === "ON_HOLD") return <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]"><AlertTriangle className="w-2.5 h-2.5 mr-1" />On Hold</Badge>;
    if (st === "OPEN") return <Badge className="bg-amber-100 text-amber-700 text-[10px]">Open</Badge>;
    if (st === "IN_PROGRESS") return <Badge className="bg-blue-100 text-blue-700 text-[10px]">In Progress</Badge>;
    if (st === "RESOLVED") return <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Resolved</Badge>;
    return <Badge variant="outline" className="text-[10px]">{s}</Badge>;
  };

  const actionLabel = actionDialog?.action === "approve" ? "Approve" : actionDialog?.action === "reject" ? "Reject" : "On Hold";
  const actionColor = actionDialog?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : actionDialog?.action === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700";

  const ActionButtons = ({ type, id, label, showHold = true }) => (
    <div className="flex flex-col gap-1.5 shrink-0">
      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openAction(type, id, "approve", label)} data-testid={`approve-${type}-${id}`}>
        <CheckCircle className="w-3 h-3 mr-1" /> Approve
      </Button>
      <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => openAction(type, id, "reject", label)} data-testid={`reject-${type}-${id}`}>
        <XCircle className="w-3 h-3 mr-1" /> Reject
      </Button>
      {showHold && (
        <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30" onClick={() => openAction(type, id, "hold", label)} data-testid={`hold-${type}-${id}`}>
          <Clock className="w-3 h-3 mr-1" /> Hold
        </Button>
      )}
      {(type === "order" || type === "dealer") && (
        <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => viewHistory(type, id)} data-testid={`history-${type}-${id}`}>
          <Eye className="w-3 h-3 mr-1" /> History
        </Button>
      )}
    </div>
  );

  const HoldNote = ({ reason }) => reason ? (
    <div className="mt-2 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300 flex items-start gap-1">
      <StickyNote className="w-3 h-3 mt-0.5 shrink-0" /> {reason}
    </div>
  ) : null;

  const filterItems = (items) => {
    // Apply region filter first, then text search
    const inRegion = filterByRegion(items, (i) => i.region_id);
    if (!search) return inRegion;
    const q = search.toLowerCase();
    return inRegion.filter(i => JSON.stringify(i).toLowerCase().includes(q));
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Sora']">Approvals</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Review orders, dealers, expenses, leaves, and grievances</p>
        </div>
        <div className="flex items-center gap-3">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="approvals-region-switcher"
          />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-48" data-testid="approval-search" />
          </div>
          <Badge className="bg-[#8B1A1A] text-white px-3 py-1 text-sm">{counts.total || 0} Pending</Badge>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 dark:bg-slate-800 flex-wrap">
          <TabsTrigger value="orders" data-testid="tab-orders" className="gap-1.5"><ShoppingCart className="w-3.5 h-3.5" /> Orders <Badge variant="outline" className="ml-1 text-[10px]">{counts.orders || 0}</Badge></TabsTrigger>
          <TabsTrigger value="dealers" data-testid="tab-dealers" className="gap-1.5"><Store className="w-3.5 h-3.5" /> Dealers <Badge variant="outline" className="ml-1 text-[10px]">{counts.dealers || 0}</Badge></TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses" className="gap-1.5"><Receipt className="w-3.5 h-3.5" /> Expenses <Badge variant="outline" className="ml-1 text-[10px]">{counts.expenses || 0}</Badge></TabsTrigger>
          <TabsTrigger value="leaves" data-testid="tab-leaves" className="gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Leaves <Badge variant="outline" className="ml-1 text-[10px]">{counts.leaves || 0}</Badge></TabsTrigger>
          <TabsTrigger value="grievances" data-testid="tab-grievances" className="gap-1.5"><Flag className="w-3.5 h-3.5" /> Grievances <Badge variant="outline" className="ml-1 text-[10px]">{counts.grievances || 0}</Badge></TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-4">
          {loading ? <div className="text-center py-12"><div className="w-8 h-8 spinner mx-auto" /></div> :
          filterItems(data.orders).length === 0 ? <Card className="p-12 text-center dark:bg-slate-800"><ShoppingCart className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No pending orders</p></Card> :
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filterItems(data.orders).map((o) => (
              <Card key={o.order_id} className="p-4 dark:bg-slate-800 dark:border-slate-700" data-testid={`approval-order-${o.order_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <ShoppingCart className="w-4 h-4 text-[#8B1A1A]" />
                      <span className="font-mono font-semibold text-sm dark:text-white">{o.order_id}</span>
                      {getStatusBadge(o.status)}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{o.party_name || o.party_id} &middot; {o.items?.length || 0} items &middot; {new Date(o.order_date || o.created_at).toLocaleDateString("en-IN")}</p>
                    <p className="text-sm font-bold text-[#8B1A1A] mt-1">&#8377;{(o.total_amount || 0).toLocaleString("en-IN")}</p>
                    <HoldNote reason={o.hold_reason} />
                    {o.placed_by_name && <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1"><User className="w-2.5 h-2.5" /> {o.placed_by_name}</p>}
                  </div>
                  <ActionButtons type="order" id={o.order_id} label={o.order_id} />
                </div>
              </Card>
            ))}
          </div>}
        </TabsContent>

        {/* Dealers Tab */}
        <TabsContent value="dealers" className="mt-4">
          {loading ? <div className="text-center py-12"><div className="w-8 h-8 spinner mx-auto" /></div> :
          filterItems(data.dealers).length === 0 ? <Card className="p-12 text-center dark:bg-slate-800"><Store className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No pending dealers</p></Card> :
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filterItems(data.dealers).map((d) => (
              <Card key={d.party_id} className="p-4 dark:bg-slate-800 dark:border-slate-700" data-testid={`approval-dealer-${d.party_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Store className="w-4 h-4 text-purple-600" />
                      <span className="font-semibold text-sm dark:text-white">{d.firm_name}</span>
                      {getStatusBadge(d.approval_status)}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{d.party_code} &middot; {d.contact_person} &middot; {d.city || d.state || ""}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{d.phone} &middot; {d.email}</p>
                    <HoldNote reason={d.hold_reason} />
                  </div>
                  <ActionButtons type="dealer" id={d.party_id} label={d.firm_name} />
                </div>
              </Card>
            ))}
          </div>}
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="mt-4">
          {loading ? <div className="text-center py-12"><div className="w-8 h-8 spinner mx-auto" /></div> :
          filterItems(data.expenses).length === 0 ? <Card className="p-12 text-center dark:bg-slate-800"><Receipt className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No pending expenses</p></Card> :
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filterItems(data.expenses).map((e) => (
              <Card key={e.expense_id} className="p-4 dark:bg-slate-800 dark:border-slate-700" data-testid={`approval-expense-${e.expense_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Receipt className="w-4 h-4 text-amber-600" />
                      <span className="font-semibold text-sm dark:text-white">{e.category}</span>
                      {getStatusBadge(e.status)}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{e.employee_name} &middot; {e.date}</p>
                    <p className="text-sm font-bold text-amber-600 mt-1">&#8377;{(e.amount || 0).toLocaleString("en-IN")}</p>
                    {e.description && <p className="text-xs text-slate-500 mt-1">{e.description}</p>}
                  </div>
                  <ActionButtons type="expense" id={e.expense_id} label={e.category} showHold={false} />
                </div>
              </Card>
            ))}
          </div>}
        </TabsContent>

        {/* Leaves Tab */}
        <TabsContent value="leaves" className="mt-4">
          {loading ? <div className="text-center py-12"><div className="w-8 h-8 spinner mx-auto" /></div> :
          filterItems(data.leaves).length === 0 ? <Card className="p-12 text-center dark:bg-slate-800"><CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No pending leave requests</p></Card> :
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filterItems(data.leaves).map((l) => (
              <Card key={l.leave_id} className="p-4 dark:bg-slate-800 dark:border-slate-700" data-testid={`approval-leave-${l.leave_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CalendarDays className="w-4 h-4 text-indigo-600" />
                      <span className="font-semibold text-sm dark:text-white">{l.employee_name || l.employee_id}</span>
                      {getStatusBadge(l.status)}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px]">{l.leave_type}</Badge>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{l.from_date} to {l.to_date} ({l.days} day{l.days > 1 ? "s" : ""})</span>
                    </div>
                    {l.reason && <p className="text-xs text-slate-500 mt-1.5 italic">"{l.reason}"</p>}
                    <HoldNote reason={l.approval_notes && l.status === "ON_HOLD" ? l.approval_notes : null} />
                  </div>
                  <ActionButtons type="leave" id={l.leave_id} label={l.employee_name || l.leave_id} showHold={true} />
                </div>
              </Card>
            ))}
          </div>}
        </TabsContent>

        {/* Grievances Tab */}
        <TabsContent value="grievances" className="mt-4">
          {loading ? <div className="text-center py-12"><div className="w-8 h-8 spinner mx-auto" /></div> :
          filterItems(data.grievances).length === 0 ? <Card className="p-12 text-center dark:bg-slate-800"><Flag className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No open grievances</p></Card> :
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filterItems(data.grievances).map((g) => (
              <Card key={g.grievance_id} className="p-4 dark:bg-slate-800 dark:border-slate-700" data-testid={`grievance-${g.grievance_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Flag className="w-4 h-4 text-red-600" />
                      <span className="font-semibold text-sm dark:text-white">{g.subject}</span>
                      <PriorityBadge priority={g.priority} />
                      {getStatusBadge(g.status)}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{g.submitted_by_name} &middot; {g.grievance_type} &middot; {new Date(g.created_at).toLocaleDateString("en-IN")}</p>
                    {g.description && <p className="text-xs text-slate-600 dark:text-slate-400 mt-1.5 line-clamp-2">{g.description}</p>}
                    {g.resolution && (
                      <div className="mt-2 px-2 py-1 bg-emerald-50 dark:bg-emerald-900/20 rounded text-xs text-emerald-700 dark:text-emerald-300">Resolution: {g.resolution}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={() => { setGrievanceDialog(g); setGrievanceUpdate({ status: g.status, resolution: g.resolution || "", assigned_to: g.assigned_to || "" }); }} data-testid={`manage-grievance-${g.grievance_id}`}>
                      <MessageSquare className="w-3 h-3 mr-1" /> Manage
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>}
        </TabsContent>
      </Tabs>

      {/* Action Dialog (approve/reject/hold) */}
      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionDialog?.action === "approve" && <CheckCircle className="w-5 h-5 text-emerald-600" />}
              {actionDialog?.action === "reject" && <XCircle className="w-5 h-5 text-red-600" />}
              {actionDialog?.action === "hold" && <Clock className="w-5 h-5 text-blue-600" />}
              {actionLabel} — {actionDialog?.itemLabel}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {actionDialog?.action === "approve" && (
              <div className="flex items-start gap-2 p-2.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-xs text-emerald-700 dark:text-emerald-300">Approval notes visible to <strong>Plant Admin / Factory Admin</strong></p>
              </div>
            )}
            {actionDialog?.action === "reject" && (
              <div className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700 dark:text-red-300">Rejection notes visible to the <strong>Employee who placed the request</strong></p>
              </div>
            )}
            {actionDialog?.action === "hold" && (
              <div className="flex items-start gap-2 p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <Clock className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300">Hold notes visible to <strong>all parties</strong></p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Notes / Tags</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                placeholder={actionDialog?.action === "approve" ? "e.g. Verified, credit limit OK..." : actionDialog?.action === "reject" ? "e.g. Incomplete docs, exceeded limit..." : "e.g. Pending verification..."}
                className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 text-sm rounded-md dark:bg-slate-800 dark:text-white" data-testid="action-notes" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setActionDialog(null)} className="flex-1">Cancel</Button>
              <Button onClick={handleAction} disabled={processing} className={`flex-1 text-white ${actionColor}`} data-testid="confirm-action-btn">
                {processing ? "Processing..." : `Confirm ${actionLabel}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Grievance Manage Dialog */}
      <Dialog open={!!grievanceDialog} onOpenChange={() => setGrievanceDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="grievance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-600" /> Manage Grievance
            </DialogTitle>
          </DialogHeader>
          {grievanceDialog && (
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm dark:text-white">{grievanceDialog.subject}</span>
                  <PriorityBadge priority={grievanceDialog.priority} />
                </div>
                <p className="text-xs text-slate-500">{grievanceDialog.submitted_by_name} &middot; {grievanceDialog.grievance_type}</p>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{grievanceDialog.description}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Status</label>
                <Select value={grievanceUpdate.status} onValueChange={(v) => setGrievanceUpdate({ ...grievanceUpdate, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="grievance-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPEN">Open</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="RESOLVED">Resolved</SelectItem>
                    <SelectItem value="CLOSED">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Assign To</label>
                <Input value={grievanceUpdate.assigned_to} onChange={(e) => setGrievanceUpdate({ ...grievanceUpdate, assigned_to: e.target.value })} placeholder="e.g. HR Manager" className="mt-1" data-testid="grievance-assign" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Resolution Notes</label>
                <textarea value={grievanceUpdate.resolution} onChange={(e) => setGrievanceUpdate({ ...grievanceUpdate, resolution: e.target.value })} rows={3} placeholder="Describe resolution or action taken..."
                  className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 text-sm rounded-md dark:bg-slate-800 dark:text-white" data-testid="grievance-resolution" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setGrievanceDialog(null)} className="flex-1">Cancel</Button>
                <Button onClick={handleGrievanceUpdate} disabled={processing} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-grievance-btn">
                  {processing ? "Updating..." : "Update Grievance"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyDialog} onOpenChange={() => setHistoryDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="history-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-slate-600" /> Approval History</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {historyData.length === 0 ? <p className="text-sm text-slate-500 text-center py-4">No history yet</p> :
            historyData.map((h, i) => (
              <div key={i} className={`p-3 rounded-lg border text-xs ${h.action === "APPROVED" ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200" : h.action === "REJECTED" ? "bg-red-50 dark:bg-red-900/20 border-red-200" : "bg-blue-50 dark:bg-blue-900/20 border-blue-200"}`} data-testid={`history-entry-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    {h.action === "APPROVED" && <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
                    {h.action === "REJECTED" && <XCircle className="w-3.5 h-3.5 text-red-600" />}
                    {h.action === "ON_HOLD" && <Clock className="w-3.5 h-3.5 text-blue-600" />}
                    <span className="font-semibold">{h.action}</span>
                  </div>
                  <span className="text-slate-400">{h.timestamp ? new Date(h.timestamp).toLocaleString("en-IN") : ""}</span>
                </div>
                <p className="text-slate-600 dark:text-slate-300">By: <strong>{h.by}</strong></p>
                {h.notes && <p className="text-slate-600 dark:text-slate-300 mt-1 italic">"{h.notes}"</p>}
                <p className="text-slate-400 mt-1">Visible to: {(h.visible_to || []).join(", ")}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalsPage;
